import { Building2, Users, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import brazzavilleCommunity from '@/assets/brazzaville-community.jpg';

export function CTASection() {
  const sectionRef = useScrollAnimation();
  
  return (
    <section ref={sectionRef} className="relative section-padding bg-gradient-to-br from-primary via-primary-dark to-primary text-primary-foreground overflow-hidden scroll-reveal">
      {/* Authentic Brazzaville Community Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <img 
          src={brazzavilleCommunity} 
          alt="Communauté de Brazzaville" 
          className="absolute inset-0 w-full h-full object-cover opacity-15"
          style={{ objectPosition: 'center' }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/95 via-primary-dark/90 to-primary/95"></div>
      </div>
      
      {/* Refined Ambient Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none opacity-80">
        <div className="absolute top-20 -left-20 w-[600px] h-[600px] bg-primary-foreground/12 rounded-full blur-[140px] animate-float"></div>
        <div className="absolute bottom-20 -right-20 w-[700px] h-[700px] bg-primary-foreground/12 rounded-full blur-[160px] animate-float animation-delay-300"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[900px] bg-primary-foreground/6 rounded-full blur-[180px] animate-pulse"></div>
      </div>
      
      {/* Geometric Pattern Overlay */}
      <div className="absolute inset-0 opacity-10">
        <svg className="absolute bottom-0 w-full h-40" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" fill="currentColor"></path>
        </svg>
      </div>
      
      {/* Decorative Pattern Elements */}
      <div className="absolute inset-0 opacity-[0.07]">
        <div className="absolute top-10 left-10 w-40 h-40 border-4 border-primary-foreground rotate-12 rounded-2xl animate-spin-slow"></div>
        <div className="absolute bottom-32 right-24 w-32 h-32 border-4 border-primary-foreground rounded-full animate-pulse"></div>
        <div className="absolute top-1/3 right-1/4 w-24 h-24 border-4 border-primary-foreground rotate-45 rounded-lg animate-float"></div>
        <div className="absolute bottom-1/4 left-1/4 w-28 h-28 bg-primary-foreground/20 rotate-12 rounded-2xl animate-pulse animation-delay-100"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 max-w-6xl mx-auto">
          {/* For Organizations */}
          <div className="relative bg-primary-foreground/10 backdrop-blur-2xl rounded-[2rem] p-12 text-center hover:bg-primary-foreground/15 transition-all duration-500 border-2 border-primary-foreground/20 hover:border-primary-foreground/50 hover:shadow-[0_20px_80px_rgba(0,0,0,0.3)] hover:-translate-y-6 hover:rotate-1 group overflow-hidden animate-fade-in-up">
            {/* Animated gradient glow */}
            <div className="absolute inset-0 rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary-foreground/15 via-transparent to-primary-foreground/10"></div>
            
            {/* Icon container with gradient background */}
            <div className="relative z-10 mb-8 inline-block">
              <div className="w-28 h-28 rounded-3xl bg-gradient-to-br from-primary-foreground/20 to-primary-foreground/5 flex items-center justify-center mx-auto group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl">
                <Building2 className="h-14 w-14 text-primary-foreground" strokeWidth={2.5} />
              </div>
            </div>
            
            <h3 className="text-4xl font-extrabold mb-5 relative z-10 tracking-tight">
              Vous recrutez ? Malamu! 🎯
            </h3>
            <p className="text-primary-foreground/95 font-light mb-10 relative z-10 text-xl leading-relaxed">
              Publiez vos offres gratuitement et trouvez les meilleurs talents de Brazzaville en quelques clics.
            </p>
            <Link to="/publish-opportunity">
              <Button variant="secondary" size="xl" className="w-full relative z-10 text-lg font-bold hover:scale-110 hover:-translate-y-1 transition-all shadow-2xl">
                Publier une offre gratuitement
                <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </div>
          
          {/* For Candidates */}
          <div className="relative bg-primary-foreground/10 backdrop-blur-2xl rounded-[2rem] p-12 text-center hover:bg-primary-foreground/15 transition-all duration-500 border-2 border-primary-foreground/20 hover:border-primary-foreground/50 hover:shadow-[0_20px_80px_rgba(0,0,0,0.3)] hover:-translate-y-6 hover:-rotate-1 group overflow-hidden animate-fade-in-up animation-delay-200">
            {/* Animated gradient glow */}
            <div className="absolute inset-0 rounded-[2rem] opacity-0 group-hover:opacity-100 transition-opacity duration-500 bg-gradient-to-br from-primary-foreground/10 via-transparent to-primary-foreground/15"></div>
            
            {/* Icon container with gradient background */}
            <div className="relative z-10 mb-8 inline-block">
              <div className="w-28 h-28 rounded-3xl bg-gradient-to-br from-primary-foreground/20 to-primary-foreground/5 flex items-center justify-center mx-auto group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-2xl">
                <Users className="h-14 w-14 text-primary-foreground" strokeWidth={2.5} />
              </div>
            </div>
            
            <h3 className="text-4xl font-extrabold mb-5 relative z-10 tracking-tight">
              Vous cherchez ? Tozali awa!
            </h3>
            <p className="text-primary-foreground/95 font-light mb-10 relative z-10 text-xl leading-relaxed">
              Inscrivez-vous pour sauvegarder vos opportunités préférées et ne manquer aucune nouveauté.
            </p>
            <Link to="/opportunities">
              <Button variant="secondary" size="xl" className="w-full relative z-10 text-lg font-bold hover:scale-110 hover:-translate-y-1 transition-all shadow-2xl">
                Explorer les opportunités
                <ArrowRight className="ml-2 h-6 w-6 group-hover:translate-x-2 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}